/**
 * Hook for fetching tank parameters and feature flags.
 *
 * Provides access to the unified parameter mapping system for:
 * - Dynamic parameter-based UI rendering
 * - Conditional navigation based on device availability
 * - Feature-gated content display
 */
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useTank } from '@/contexts/TankContext';
import {
  getTankParameters,
  TankParametersResponse,
  tankParametersQueryKey,
} from '@/api/parameters';
import { CACHE_TIMES } from '@/config/cacheTimes';

interface UseTankParametersOptions {
  /** Whether to enable the query (default: true when tank is selected) */
  enabled?: boolean;
}

/**
 * Fetch parameters and feature flags for the currently selected tank.
 *
 * @param options - Query options
 * @returns Query result with parameters and features
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTankParameters();
 *
 * // Check if dosetronic is available
 * if (data?.features.has_dosetronic) {
 *   // Show dosing page link
 * }
 *
 * // Filter parameters by category
 * const sensorParams = data?.parameters.filter(p => p.category === 'sensor');
 * ```
 */
export function useTankParameters(
  options: UseTankParametersOptions = {}
): UseQueryResult<TankParametersResponse> {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  // Default to enabled when tank is selected
  const { enabled = true } = options;

  return useQuery({
    queryKey: tankParametersQueryKey(tankId),
    queryFn: () => getTankParameters(tankId!),
    enabled: enabled && !!tankId,
    // DATA (5m), NOT CONFIG: no mutation invalidates this key today, so
    // staleness is the only thing keeping feature flags fresh after a
    // device is added/removed. Persistence still paints instantly.
    staleTime: CACHE_TIMES.DATA,
    meta: { persist: true },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Get parameters filtered by category.
 *
 * @param data - Tank parameters response
 * @param category - Category to filter by ('sensor', 'water_test', 'nutrient', 'trace')
 * @returns Filtered parameters sorted by display_order
 */
export function getParametersByCategory(
  data: TankParametersResponse | undefined,
  category: string
): TankParametersResponse['parameters'] {
  if (!data?.parameters) return [];

  return data.parameters
    .filter((p) => p.category === category && p.is_enabled)
    .sort((a, b) => a.display_order - b.display_order);
}

/**
 * Check if a specific feature is available for the tank.
 *
 * @param data - Tank parameters response
 * @param feature - Feature key to check
 * @returns Whether the feature is available
 */
export function hasFeature(
  data: TankParametersResponse | undefined,
  feature: keyof TankParametersResponse['features']
): boolean {
  return data?.features?.[feature] ?? false;
}
